
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface ProfileAvatarProps {
  userInitials: string;
  avatarUrl?: string | null;
  userId?: string | null;
  onAvatarUpdate: (url: string) => void;
}

const ProfileAvatar: React.FC<ProfileAvatarProps> = ({ 
  userInitials, 
  avatarUrl, 
  userId, 
  onAvatarUpdate 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const supabase = useSupabaseClient();
  const { toast } = useToast();
  
  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file || !userId) {
      return;
    }
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "Filen är för stor",
        description: "Max filstorlek är 2MB",
        variant: "destructive"
      });
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Ogiltig filtyp",
        description: "Endast bildfiler är tillåtna",
        variant: "destructive"
      });
      return;
    }
    
    setIsUploading(true);
    
    try {
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `avatars/${fileName}`;
      
      // Upload the file to Supabase Storage - using the correct bucket name "profiles"
      const { error: uploadError } = await supabase
        .storage
        .from('profiles')
        .upload(filePath, file);
        
      if (uploadError) throw uploadError;
      
      // Get the public URL for the file
      const { data } = supabase
        .storage
        .from('profiles')
        .getPublicUrl(filePath);
        
      // Update user profile with new avatar URL
      onAvatarUpdate(data.publicUrl);
      
      toast({
        title: "Bild uppladdad",
        description: "Din profilbild har uppdaterats",
      });
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast({
        title: "Ett fel uppstod",
        description: error.message || "Kunde inte ladda upp bilden. Försök igen.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleRemoveAvatar = async () => {
    if (!userId) return;
    
    try {
      onAvatarUpdate('');
      
      toast({
        title: "Bild borttagen",
        description: "Din profilbild har tagits bort",
      });
    } catch (error: any) {
      console.error('Error removing avatar:', error);
      toast({
        title: "Ett fel uppstod",
        description: error.message || "Kunde inte ta bort bilden. Försök igen.",
        variant: "destructive"
      });
    }
  };
  
  return (
    <div className="flex flex-col md:flex-row gap-8 items-start md:items-center">
      <Avatar className="h-24 w-24">
        {avatarUrl ? (
          <AvatarImage src={avatarUrl} alt="Profile picture" />
        ) : null}
        <AvatarFallback className="text-lg bg-yellow-500 text-darkgray">
          {userInitials}
        </AvatarFallback>
      </Avatar>
      
      <div>
        <h3 className="text-lg font-medium">Profilbild</h3>
        <p className="text-sm text-gray-500 mt-1">
          Din profilbild visas i analyserna du genomför
        </p>
        <div className="mt-3 flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            disabled={isUploading}
            onClick={() => document.getElementById('avatar-upload')?.click()}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Laddar upp...
              </>
            ) : (
              'Ändra bild'
            )}
          </Button>
          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
          />
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleRemoveAvatar}
            disabled={isUploading || !avatarUrl}
          >
            Ta bort
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileAvatar;
